import React from 'react'
import { Link } from 'react-router-dom'
import { routePath, cityPath } from '../../../utils/urls'

/**
 * Crawlable entry point into the route and city pages.
 *
 * The "Popular Routes" carousel above renders its cards as buttons that
 * prefill the booking form, so the homepage shipped without a single <a> to
 * /routes/* or /cities/* — the programmatic pages had no path in from the
 * strongest page on the site. These are plain links on purpose.
 *
 * Every entry below is a page we prerender (see scripts/seo/tier1.config.mjs);
 * linking to a route whose cities have no CMS content would just point at
 * boilerplate.
 */
const ROUTES = [
  ['delhi', 'agra'],
  ['delhi', 'jaipur'],
  ['delhi', 'chandigarh'],
  ['delhi', 'dehradun'],
  ['delhi', 'shimla'],
  ['delhi', 'lucknow'],
  ['delhi', 'mathura'],
  ['delhi', 'amritsar'],
  ['jaipur', 'delhi'],
  ['jaipur', 'agra'],
  ['jaipur', 'udaipur'],
  ['jaipur', 'jodhpur'],
  ['agra', 'delhi'],
  ['agra', 'jaipur'],
  ['lucknow', 'varanasi'],
  ['lucknow', 'kanpur'],
  ['chandigarh', 'shimla'],
  ['chandigarh', 'amritsar'],
  ['pune', 'nashik'],
  ['pune', 'aurangabad'],
  ['ahmedabad', 'surat'],
  ['ahmedabad', 'vadodara'],
  ['bengaluru', 'mysore'],
  ['bengaluru', 'chennai'],
  ['bengaluru', 'coimbatore'],
  ['chennai', 'tirupati'],
  ['chennai', 'puducherry'],
  ['kochi', 'thiruvananthapuram'],
  ['secunderabad', 'vijayawada'],
  ['kolkata', 'siliguri'],
  ['kolkata', 'puri'],
  ['indore', 'bhopal'],
]

// Every city hub we prerender (see scripts/seo/tier1.config.mjs REGIONS). Each
// hub links to every Tier-1 route out of that city, so linking all 120 hubs
// from the homepage — the strongest page on the site — puts every route page
// two clicks deep. That is the fix for the ~800 route pages Google left in
// "Discovered - currently not indexed": they were reachable only from hubs that
// the homepage itself did not link, so they sat too deep to earn a crawl.
const CITIES = [
  'agra', 'ahmedabad', 'ajmer', 'alappuzha', 'allahabad', 'alwar', 'amreli',
  'amritsar', 'anand', 'asansol', 'aurangabad', 'belgaum', 'bengaluru',
  'bharatpur', 'bharuch', 'bhavnagar', 'bhilwara', 'bhopal', 'bhuj', 'bikaner',
  'botad', 'chandigarh', 'chennai', 'chittorgarh', 'coimbatore', 'cuttack',
  'deesa', 'dehradun', 'delhi', 'dhanbad', 'durgapur', 'dwarka', 'faridabad',
  'gandhidham', 'gandhinagar', 'gir', 'goa', 'godhra', 'gondal', 'gorakhpur',
  'gurugram', 'guwahati', 'gwalior', 'indore', 'jabalpur', 'jaipur', 'jaisalmer',
  'jalandhar', 'jammu', 'jamnagar', 'jamshedpur', 'jodhpur', 'junagadh', 'kalol',
  'kanpur', 'kochi', 'kolhapur', 'kolkata', 'kota', 'kozhikode', 'kumbhalgarh',
  'lucknow', 'ludhiana', 'madurai', 'mandawa', 'mangalore', 'mathura', 'meerut',
  'mehsana', 'morbi', 'mount-abu', 'mumbai', 'mysore', 'nadiad', 'nagpur',
  'nashik', 'nathdwara', 'navsari', 'neemrana', 'noida', 'palanpur', 'pali',
  'patan', 'patiala', 'patna', 'porbandar', 'puducherry', 'pune', 'puri',
  'pushkar', 'raipur', 'rajkot', 'ranchi', 'salem', 'saputara', 'sawai-madhopur',
  'secunderabad', 'shillong', 'shimla', 'sikar', 'siliguri', 'solapur',
  'somnath', 'statue-of-unity', 'surat', 'thiruvananthapuram', 'thrissur',
  'tiruchirappalli', 'tirupati', 'udaipur', 'ujjain', 'vadodara', 'valsad',
  'vapi', 'varanasi', 'vellore', 'veraval', 'vijayawada', 'visakhapatnam',
  'warangal',
]

const label = (slug) =>
  slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

const PopularLinks = () => (
  <section className="popular-links">
    <div className="popular-links__group">
      <h2 className="popular-links__heading">More intercity cab routes</h2>
      <ul className="popular-links__list">
        {ROUTES.map(([from, to]) => (
          <li key={`${from}-${to}`}>
            <Link className="popular-links__link" to={routePath(from, to)}>
              {label(from)} to {label(to)} Cab
            </Link>
          </li>
        ))}
      </ul>
    </div>

    <div className="popular-links__group">
      <h2 className="popular-links__heading">Cabs by city</h2>
      <ul className="popular-links__list">
        {CITIES.map((city) => (
          <li key={city}>
            <Link className="popular-links__link" to={cityPath(city)}>
              Cabs in {label(city)}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </section>
)

export default PopularLinks
